import { Icon } from '@/lib/icons'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useProjects, useTasks, useAllEdges } from '@/db/hooks'
import { nest } from '@/lib/steps'
import { projectKind } from '@/kinds/project'
import { Button, Empty, PageTitle } from '@/components/ui'
import { Pin } from 'lucide-react'
import { tokens, type Hue } from '@/lib/colour'

export const ProjectsView = () => {
  const projects = useProjects() ?? []
  const tasks = useTasks() ?? []
  const byId = new Map(tasks.map((t) => [t.id, t]))
  const [adding, setAdding] = useState(false)
  const active = projects
    .filter((p) => p.status.state === 'active')
    .sort((a, b) => a.spec.title.localeCompare(b.spec.title))
  const paused = projects.filter((p) => p.status.state === 'paused')
  const edges = useAllEdges()
  const Form = projectKind.Form
  return (
    <div>
      <PageTitle right={<Button onClick={() => setAdding(true)}>New project</Button>}>Projects</PageTitle>
      {active.length === 0 && paused.length === 0 && (
        <Empty>No projects. Projects hold tasks that share an outcome.</Empty>
      )}
      <ul className="group-list" data-testid="projects-list">
        {nest(active, edges, 'belongs_to').map(({ item: p, child }) => {
          const next = p.status.next_task_id ? byId.get(p.status.next_task_id) : undefined
          return (
            <li key={p.id} className={child ? 'pl-7' : undefined}>
              <Link
                to="/projects/$id"
                params={{ id: p.id }}
                className="block px-3.5 py-2.5 min-h-12 hover:bg-raised/50"
              >
                <div className="font-medium flex items-center gap-2">
                  {p.spec.colour && (
                    <span
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ background: tokens(p.spec.colour as Hue).accent }}
                    />
                  )}
                  <Icon value={p.spec.icon} size={18} />
                  {p.spec.title}
                  {p.spec.pinned && (
                    <Pin
                      size={13}
                      className="text-faint shrink-0"
                      aria-label="Pinned"
                      data-testid="project-pinned"
                    />
                  )}
                </div>
                {next ? (
                  <div className="text-sm text-muted truncate">→ {next.spec.title}</div>
                ) : (
                  <div className="text-sm text-faint">No next task</div>
                )}
              </Link>
            </li>
          )
        })}
      </ul>
      {paused.length > 0 && (
        <section className="mt-6">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Paused</h2>
          {/* the heading says they are paused; the rows need not say it again */}
          <ul className="group-list">
            {paused.map((p) => (
              <li key={p.id}>
                <Link
                  to="/projects/$id"
                  params={{ id: p.id }}
                  className="flex items-center gap-2 px-3.5 py-2.5 min-h-12 text-muted"
                >
                  <Icon value={p.spec.icon} size={18} />
                  {p.spec.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {adding && <Form onDone={() => setAdding(false)} />}
    </div>
  )
}
